// The app's data with undo/redo, kept in sync with the server.
//
// - Every change goes through commit(label, fn): fn gets the current data and returns the new data (or throws an
//   Error whose message is shown to the person; nothing changes then).
// - The server holds the copy every device shares. This browser keeps a copy too (localStorage), so the app opens
//   instantly and keeps working offline; changes are sent when the server is reachable again.
// - When another device saved first, both sets of changes are merged (see lib/merge.js), including into the undo
//   history, so undoing something here never brings back an old copy of what someone else changed.

import { useCallback, useEffect, useReducer, useRef, useState } from 'react';
import { initialState, migrate } from './lib/state.js';
import { merge3, same } from './lib/merge.js';

const HISTORY_LIMIT = 100;
const RETRY_MS = 5000;

const load = (key) => { try { const s = localStorage.getItem(key); return s ? JSON.parse(s) : null; } catch { return null; } };
const save = (key, v) => { try { localStorage.setItem(key, JSON.stringify(v)); } catch {} };

function reducer(h, a) {
  switch (a.type) {
    case 'commit':
      return { past: [...h.past.slice(-HISTORY_LIMIT + 1), { data: h.present, label: a.label }], present: a.next, future: [] };
    case 'undo': {
      if (!h.past.length) return h;
      const prev = h.past[h.past.length - 1];
      return { past: h.past.slice(0, -1), present: prev.data, future: [{ data: h.present, label: prev.label }, ...h.future] };
    }
    case 'redo': {
      if (!h.future.length) return h;
      const next = h.future[0];
      return { past: [...h.past, { data: h.present, label: next.label }], present: next.data, future: h.future.slice(1) };
    }
    case 'rebase': {
      const fix = (d) => migrate(merge3(a.base, d, a.theirs));
      const present = fix(h.present);
      return {
        past: h.past.map((x) => ({ ...x, data: fix(x.data) })),
        present: same(present, h.present) ? h.present : present,
        future: h.future.map((x) => ({ ...x, data: fix(x.data) })),
      };
    }
    case 'replace':
      return { past: [], present: a.next, future: [] };
    default:
      return h;
  }
}

export function useStore({ api, prefix = 'js', onNotice = () => {} }) {
  const CACHE = `${prefix}_state_v1`;
  const BASE = `${prefix}_base_v1`;
  const [h, dispatch] = useReducer(reducer, null, () => {
    const cached = load(CACHE);
    return { past: [], present: migrate(cached || initialState()), future: [] };
  });
  const hadLocal = useRef(load(CACHE) !== null);
  const hadLocalAtStart = useRef(hadLocal.current);
  const [loaded, setLoaded] = useState(false); // first answer from the server (or the failure) has arrived
  const presentRef = useRef(h.present);
  presentRef.current = h.present;
  const historyRef = useRef(h);
  historyRef.current = h;
  const noticeRef = useRef(onNotice);
  noticeRef.current = onNotice;

  useEffect(() => { save(CACHE, h.present); }, [CACHE, h.present]);

  /* ── Changes ── */
  const commit = useCallback((label, fn) => {
    let next;
    try {
      next = fn(presentRef.current);
    } catch (e) {
      return { error: e.message || 'Something went wrong' };
    }
    if (!next || next === presentRef.current) return { ok: true, unchanged: true };
    presentRef.current = next; // so a second commit in the same moment builds on this one
    dispatch({ type: 'commit', next, label });
    return { ok: true };
  }, []);
  const undo = useCallback(() => {
    const top = historyRef.current.past[historyRef.current.past.length - 1];
    dispatch({ type: 'undo' });
    return top?.label || null;
  }, []);
  const redo = useCallback(() => {
    const top = historyRef.current.future[0];
    dispatch({ type: 'redo' });
    return top?.label || null;
  }, []);

  /* ── Server sync ── */
  const server = useRef(null); // { id, version, data } the server state `present` is based on
  const pushing = useRef(false);
  const [tick, bump] = useReducer((n) => n + 1, 0);
  const [sync, setSync] = useState(api ? 'connecting' : 'local');
  const [lastSaved, setLastSaved] = useState(null);

  const adopt = useCallback((remote) => {
    if (!remote || typeof remote !== 'object') return;
    const known = server.current || load(BASE);
    if (remote.data === null || remote.data === undefined) {
      // A new, empty server: it takes what this browser has
      server.current = { id: remote.id, version: remote.version, data: null };
      bump();
      return;
    }
    const theirs = migrate(remote.data);
    if (known && known.id === remote.id && known.data) {
      if (remote.version <= known.version && server.current) return; // nothing new
      dispatch({ type: 'rebase', base: known.data, theirs });
    } else {
      // Never synced with this server (or the server's data was replaced): the server's copy wins.
      // Whatever this browser had that the server doesn't is kept aside so nothing is lost.
      const local = presentRef.current;
      if (hadLocal.current && !same(local, theirs)) {
        save(`${prefix}_aside_${Date.now()}`, local);
        noticeRef.current("This device had changes the server doesn't know about. They were kept aside — see Settings → Backups.");
      }
      dispatch({ type: 'replace', next: theirs });
      presentRef.current = theirs;
    }
    hadLocal.current = true;
    server.current = { id: remote.id, version: remote.version, data: theirs };
    save(BASE, server.current);
    bump();
  }, [BASE, prefix]);

  const pull = useCallback(async () => {
    if (!api) return;
    try {
      adopt(await api.getState());
      setSync((s) => (s === 'saving' ? s : 'online'));
    } catch (e) {
      if (e.status === 401) setSync('signed-out');
      else setSync('offline');
    } finally {
      setLoaded(true);
    }
  }, [api, adopt]);

  // Load, and listen for other devices
  useEffect(() => {
    if (!api) return undefined;
    let alive = true;
    pull();
    const unsub = api.subscribe((msg) => {
      if (!alive || !msg) return;
      const s = server.current;
      if (msg.version !== undefined && (!s || msg.id !== s.id || msg.version > s.version)) pull();
    }, () => alive && setSync((x) => (x === 'online' ? 'offline' : x)));
    const onVisible = () => { if (document.visibilityState === 'visible') pull(); };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('online', pull);
    return () => { alive = false; unsub(); document.removeEventListener('visibilitychange', onVisible); window.removeEventListener('online', pull); };
  }, [api, pull]);

  // Push local changes
  useEffect(() => {
    if (!api || !server.current || pushing.current) return undefined;
    const base = server.current;
    const data = presentRef.current;
    if (base.data && same(base.data, data)) return undefined;
    pushing.current = true;
    setSync('saving');
    let retry = null;
    api.putState({ id: base.id, baseVersion: base.version, data })
      .then((res) => {
        server.current = { id: res.id, version: res.version, data };
        save(BASE, server.current);
        setSync('online');
        setLastSaved(Date.now());
      })
      .catch((e) => {
        if (e.status === 409 && e.body) { adopt(e.body); setSync('online'); return; }
        if (e.status === 401) { setSync('signed-out'); return; }
        setSync('offline');
        retry = setTimeout(() => { pull(); bump(); }, RETRY_MS);
      })
      .finally(() => { pushing.current = false; bump(); });
    return () => { if (retry) clearTimeout(retry); };
  }, [api, h.present, tick, adopt, pull, BASE]);

  return {
    data: h.present,
    commit,
    undo,
    redo,
    canUndo: h.past.length > 0,
    canRedo: h.future.length > 0,
    undoLabel: h.past[h.past.length - 1]?.label || null,
    redoLabel: h.future[0]?.label || null,
    sync,
    lastSaved,
    // false only on a device with nothing saved yet, until the server's data arrives (so nothing typed is lost)
    ready: !api || hadLocalAtStart.current || loaded,
    refresh: pull,
    replaceAll: (next) => { const d = migrate(next); presentRef.current = d; dispatch({ type: 'commit', next: d, label: 'Restore backup' }); },
  };
}

// Copies of local data that were kept aside after a conflict with the server
export function asideCopies(prefix = 'js') {
  const out = [];
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith(`${prefix}_aside_`)) out.push({ key: k, at: Number(k.split('_').pop()) });
    }
  } catch {}
  return out.sort((a, b) => b.at - a.at);
}
